import * as CertificationWatermark from '@tableau/certification-watermark';
import * as React from 'react';
import { Messages } from '../../../codegen/Localize';
import { imgPath } from '../../../common/utilities/ImgUrl';
import { L10n } from '../../../ts/L10n/L10n';
import { UserMapper } from '../../../ts/mappers/UserMapper';
import { ISpaceItemDetailInfo } from '../../../ts/models/ISpaceItemDetailInfo';
import { IUserInfo } from '../../../ts/models/IUserInfo';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { HiddenUserStyles } from '../SpacesStyles';
import { SpaceHeaderTestIds } from './SpaceHeader';
import { SpaceHeaderStyles } from './SpaceHeaderStyles';
import { CommonStyles } from '../../../common/react/CommonStyles';

interface UserDisplayProps {
  displayName: string;
  url: string;
  displayAsText: boolean;
}

function UserDisplay(props: UserDisplayProps) {
  if (props.displayAsText) {
    return (
    <span className={HiddenUserStyles.HiddenUserClassName}
      data-tb-test-id={SpaceHeaderTestIds.hiddenOwnerName}>
      {props.displayName}
    </span>);
  } else {
    return (
      <a className={SpaceHeaderStyles.headerLink}
        data-tb-test-id={SpaceHeaderTestIds.ownerName}
        href={props.url}>
        {props.displayName}
      </a>
    );
  }
}

function CertifierDisplay(props: UserDisplayProps) {
  if (props.displayAsText){
    return (
      <span className={HiddenUserStyles.HiddenUserClassName}
        data-tb-test-id={SpaceHeaderTestIds.certifiedBy}>
        {props.displayName}
      </span>);
  } else {
    return (
      <a className={SpaceHeaderStyles.headerLink}
        data-tb-test-id={SpaceHeaderTestIds.certifiedBy}
        href={props.url}>
        {props.displayName}
      </a>
    );
  }
}

export function OwnerMetadata(owner: IUserInfo, urls: SpacesSiteUrls): JSX.Element {

  return (owner && owner.displayName && (<React.Fragment>
      <span className={SpaceHeaderStyles.UserLabel}>
        {Messages.owner()}
      </span>
      <UserDisplay url={urls.userUrl(owner.username, owner.domainName)}
                   displayAsText={owner.isHiddenInternalUser}
                   displayName={owner.displayName} />
  </React.Fragment>));
}

export function ModifiedDateMetadata(modifiedAt: Date, withLeftPadding: boolean = true): JSX.Element {
  return <React.Fragment>
    <span className={withLeftPadding? SpaceHeaderStyles.TextOnlyMetadataLabelWithLeftMargin : SpaceHeaderStyles.TextLabelWithNoLeftMargin}>
      {Messages.modified()}
    </span>
    <span className={SpaceHeaderStyles.MetadataInfo}
          data-tb-test-id={SpaceHeaderTestIds.modifiedAt}>
      {L10n.localizedDateTime(modifiedAt)}
    </span>
  </React.Fragment>;
}

export function CertificationMetadata(
    datasourceInfo: ISpaceItemDetailInfo<'datasource'>,
    urls: SpacesSiteUrls
): JSX.Element {
  const isCertified = datasourceInfo.certificationDetails && datasourceInfo.certificationDetails.isCertified;
  const { certifierUrl, certifiedBy } = isCertified && getCertifierInfo(datasourceInfo, urls);
  return isCertified &&
    <React.Fragment>
      {certificationWidget(datasourceInfo, urls)}
      <span className={SpaceHeaderStyles.TextAndIconMetadataLabel}>
        {Messages.datasourceHeaderCertifier()}
      </span>
      <CertifierDisplay url={urls.userUrl(certifiedBy.username, certifiedBy.domainName)}
                   displayAsText={certifiedBy.isHiddenInternalUser}
                   displayName={certifiedBy.displayName} />
    </React.Fragment>;
}

function getCertifierInfo(datasourceInfo: ISpaceItemDetailInfo<'datasource'>, urls: SpacesSiteUrls) {
  const certifiedBy = UserMapper.mapOne(datasourceInfo.certificationDetails.certifier);
  const certifierUrl = certifiedBy.domainName ? urls.userUrl(certifiedBy.username, certifiedBy.domainName) : '';
  return { certifierUrl, certifiedBy };
}

function certificationWidget(datasourceInfo: ISpaceItemDetailInfo<'datasource'>, urls: SpacesSiteUrls): JSX.Element {
  const { certifierUrl, certifiedBy } = getCertifierInfo(datasourceInfo, urls);

  const icon = <img
    src={imgPath('Spaces_Caption_Certified.svg')}
    alt={Messages.certified()}
    className={SpaceHeaderStyles.CertificationIcon} />;

  const tooltipProps: CertificationWatermark.CertificationTooltipProps = {
    id: datasourceInfo.id,
    certifier: {
      customUserStyle: certifiedBy.isHiddenInternalUser ? HiddenUserStyles.HiddenUserStyle as React.CSSProperties : null,
      displayName: certifiedBy.displayName,
      url: certifierUrl
    },
    note: datasourceInfo.certificationDetails.certificationNote,
    date: datasourceInfo.lastRefreshedAt,
    style: { verticalAlign: 'top' },
    icon: icon
  };

  return <CertificationWatermark.CertificationTooltipComponent {...tooltipProps} />;
}

export const DefaultVerticalDividerPadding: number = 8;
export function VerticalDivider(rightPadding: number = DefaultVerticalDividerPadding, leftPadding: number = DefaultVerticalDividerPadding): JSX.Element {
  return <div style={{
    color: CommonStyles.Colors.Grey203,
    padding: `0px ${rightPadding}px 0px ${leftPadding}px`
  }}>|</div>
  ;
}


